// PIQ Advocate Engine - Opus 4.5 Build
// Offers Page - Commission offer template management

import React, { useState } from 'react'
import { 
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  DollarSign,
  Percent,
  Clock,
  Users
} from 'lucide-react'
import { 
  useOffers,
  useCreateOffer,
  useUpdateOffer,
  type AdvocateOffer
} from '../../lib/advocate-opus-data-service'

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdvocateOpusOffers() {
  const [showAll, setShowAll] = useState(false)
  const { data, isLoading, error, refetch } = useOffers(!showAll)
  
  const createOffer = useCreateOffer()
  const updateOffer = useUpdateOffer()
  
  const [editingOffer, setEditingOffer] = useState<Partial<AdvocateOffer> | null>(null)
  const [showModal, setShowModal] = useState(false)

  const handleCreateNew = () => {
    setEditingOffer({
      offer_name: '',
      commission_rate: 10,
      commission_type: 'percentage',
      return_window_days: 30,
    })
    setShowModal(true)
  }

  const handleEdit = (offer: AdvocateOffer) => {
    setEditingOffer(offer)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!editingOffer) return

    if (editingOffer.offer_id) {
      await updateOffer.mutateAsync({
        offerId: editingOffer.offer_id,
        ...editingOffer,
      })
    } else {
      await createOffer.mutateAsync(editingOffer)
    }
    
    setShowModal(false)
    setEditingOffer(null)
    refetch()
  }

  const handleToggleActive = async (offer: AdvocateOffer) => {
    await updateOffer.mutateAsync({
      offerId: offer.offer_id,
      active: !offer.active,
    })
    refetch()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-5xl mx-auto animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const offers = data?.offers || []

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Commission Offers</h1>
              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                Opus Build
              </span>
            </div>
            <p className="text-gray-500 mt-1">Manage commission templates for advocate activation</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="rounded"
              />
              Show inactive
            </label>
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              New Offer
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-8 py-6">
        {offers.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No offers created yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first commission offer to start activating advocates</p>
            <button
              onClick={handleCreateNew}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Create First Offer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {offers.map((offer) => (
              <OfferCard
                key={offer.offer_id}
                offer={offer}
                onEdit={() => handleEdit(offer)}
                onToggle={() => handleToggleActive(offer)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showModal && editingOffer && (
        <OfferModal
          offer={editingOffer}
          onChange={setEditingOffer}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingOffer(null)
          }}
          isSaving={createOffer.isPending || updateOffer.isPending}
        />
      )}
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface OfferCardProps {
  offer: AdvocateOffer
  onEdit: () => void
  onToggle: () => void
}

function OfferCard({ offer, onEdit, onToggle }: OfferCardProps) {
  return (
    <div className={`bg-white rounded-xl border p-6 ${!offer.active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{offer.offer_name}</h3>
          {offer.description && (
            <p className="text-sm text-gray-500 mt-1">{offer.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggle}
            className="p-1 hover:bg-gray-100 rounded"
            title={offer.active ? 'Deactivate' : 'Activate'}
          >
            {offer.active ? (
              <ToggleRight className="w-6 h-6 text-green-600" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-gray-400" />
            )}
          </button>
          <button
            onClick={onEdit}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-green-50 rounded">
            {offer.commission_type === 'percentage' ? (
              <Percent className="w-4 h-4 text-green-600" />
            ) : (
              <DollarSign className="w-4 h-4 text-green-600" />
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">Commission</p>
            <p className="font-semibold">
              {offer.commission_type === 'percentage' 
                ? `${offer.commission_rate}%` 
                : `$${offer.commission_rate}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 rounded">
            <Clock className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Return Window</p>
            <p className="font-semibold">{offer.return_window_days} days</p>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {offer.advocate_count || 0} advocates
        </div>
        <div>{offer.active_links || 0} active links</div>
      </div>

      {offer.min_tier && (
        <div className="mt-2 text-sm">
          <span className="text-gray-500">Min tier: </span>
          <span className={`font-medium ${
            offer.min_tier === 'A' ? 'text-green-600' :
            offer.min_tier === 'B' ? 'text-yellow-600' : 'text-gray-600'
          }`}>
            {offer.min_tier}
          </span>
        </div>
      )}
    </div>
  )
}

interface OfferModalProps {
  offer: Partial<AdvocateOffer>
  onChange: (offer: Partial<AdvocateOffer>) => void
  onSave: () => void
  onClose: () => void
  isSaving: boolean
}

function OfferModal({ offer, onChange, onSave, onClose, isSaving }: OfferModalProps) {
  const isNew = !offer.offer_id

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {isNew ? 'Create New Offer' : 'Edit Offer'}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offer Name *</label>
            <input
              type="text"
              value={offer.offer_name || ''}
              onChange={(e) => onChange({ ...offer, offer_name: e.target.value })}
              placeholder="e.g., Standard 10% Commission"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={offer.description || ''}
              onChange={(e) => onChange({ ...offer, description: e.target.value })}
              placeholder="Optional description..."
              className="w-full border rounded-lg px-3 py-2 h-20 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission Rate *</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={offer.commission_rate || ''}
                onChange={(e) => onChange({ ...offer, commission_rate: parseFloat(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission Type</label>
              <select
                value={offer.commission_type || 'percentage'}
                onChange={(e) => onChange({ ...offer, commission_type: e.target.value as 'percentage' | 'fixed' })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Window (days)</label>
              <input
                type="number"
                min="0"
                value={offer.return_window_days || 30}
                onChange={(e) => onChange({ ...offer, return_window_days: parseInt(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Tier</label>
              <select
                value={offer.min_tier || ''}
                onChange={(e) => onChange({ ...offer, min_tier: e.target.value as 'A' | 'B' | 'C' || undefined })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">Any tier</option>
                <option value="C">C or higher</option>
                <option value="B">B or higher</option>
                <option value="A">A only</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Value ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={offer.min_order_value || ''}
                onChange={(e) => onChange({ ...offer, min_order_value: parseFloat(e.target.value) || undefined })}
                placeholder="No minimum"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Commission ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={offer.max_commission || ''}
                onChange={(e) => onChange({ ...offer, max_commission: parseFloat(e.target.value) || undefined })}
                placeholder="No cap"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!offer.offer_name || offer.commission_rate === undefined || isSaving}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isNew ? 'Create Offer' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
